<script setup>
  import { defineProps, ref, onMounted, computed, nextTick } from 'vue'
  import InteractiveHistogram from '@/components/Charts/InteractiveHistogram.vue'
  import SankeyFlowChart from '@/components/Charts/SankeyFlowChart.vue'
  import SourceSelectorSankey from '@/components/Charts/SourceSelectorSankey.vue'
  import AnonymizationProviders from '@/components/IpDetails/AnonymizationProviders.vue'
  import ProviderThumbnail from '@/components/Anonymization/ProviderThumbnail.vue'
  import OsintTimeline from '@/components/IpDetails/OsintTimeline.vue'
  import SignalDetail from '@/components/Signals/SignalDetail.vue'
  import IpGeoMap from '@/components/Ip/IpGeoMap.vue'
  import Ip from '@/api/Ip'
  import Events from '@/api/Events'
  import moment from 'moment'
  import { useRouter } from 'vue-router'
  import { useMainStore } from '@/stores/main'
  import { useUserStore } from '@/stores/user'
  import { getLogTypeColor } from '@/constants/logTypes'

  const router = useRouter()
  const mainStore = useMainStore()
  const userStore = useUserStore()

  const anonymizationProviders = ref([])
  const anonLoading = ref(true)
  const histogram = ref([])
  const histogramLoading = ref(true)
  const histogramChart = ref(null)
  // source → selector sankey (which sources observed this IP), from the broad
  // all-fields search below — reuses its source_log_types, adds no query.
  const sourceCounts = ref({})
  // Sankey data - separate aggregations for accurate totals
  const sankeyInbound = ref({ portTotals: [], ipTotals: [], links: [] })
  const sankeyOutbound = ref({ portTotals: [], ipTotals: [], links: [] })
  const sankeyLoading = ref(true)
  // Log type toggle state for Sankey
  const availableLogTypes = ref([])  // Array of { log_type: string, count: number }
  const connectionLogTypes = ref([])  // Log types from connection-specific search (id.resp_h/id.orig_h)
  const connectionLogTypesLoading = ref(true)
  const selectedSankeyLogType = ref('conn')  // Default to conn

  const props = defineProps({
    data: {
      type: Object,
      default: () => ({})
    },
    // When true, suppress the internal IP identity header — the host
    // (e.g. the selector results single view) already shows the IP.
    hideIdentity: {
      type: Boolean,
      default: false
    }
  });

  // Check if user has access to event search (requires Elite tier or higher)
  // accountType 4 = Elite, 5 = Enterprise, 1337 = Admin
  const hasEventAccess = computed(() => {
    return userStore.accountType >= 4 || userStore.accountType === 1337
  })

  // Generic enrichment response shape: per-IP data lives under
  // data.enrichments.<webscout_slug>.<group>. Geo bundles location/company/as/
  // network/hostnames; osint lives under its own slug.
  const geo = computed(() => props.data.enrichments?.webscout_geo || {})
  const location = computed(() => geo.value.location)
  const company = computed(() => geo.value.company)
  const asInfo = computed(() => geo.value.as)
  const network = computed(() => geo.value.network)
  const osint = computed(() => props.data.enrichments?.webscout_osint)
  const signals = computed(() => props.data.enrichments?.webscout_signals?.signals || [])
  const abuse = computed(() => props.data.enrichments?.abuseipdb)

  // A widget is "available" when its enrichment slug is present as a key in the
  // response — the backend's present-when-granted contract (empty {} = ran, no
  // data). Absent key = no account access, so the widget is omitted. Present
  // but empty = the widget renders in its empty state rather than disappearing.
  const enrichmentsMap = computed(() => props.data.enrichments || {})
  const hasEnrichment = (slug) => slug in enrichmentsMap.value

  // Severity band for the AbuseIPDB confidence score (0 none · <25 low ·
  // <75 medium · else high), driving the score badge colour.
  const abuseSeverity = computed(() => {
    const s = abuse.value?.abuse_confidence_score
    if (typeof s !== 'number') return null
    if (s === 0) return 'none'
    if (s < 25) return 'low'
    if (s < 75) return 'medium'
    return 'high'
  })

  // Signals link to the in-app detail slideover (by id), never the external URL.
  function openSignal(sig) {
    mainStore.show_slideover(SignalDetail, { signalId: sig.id }, { width: 50 }, sig.title || 'Signal')
  }

  // Fences a batch against a newer log-type selection. Six aggregations per
  // call, so switching log types mid-flight can land the old batch last and
  // leave the chart showing one log type while selectedSankeyLogType (which
  // drives Sankey drill-in) says another — the click then navigates to an
  // Event Search scoped to the wrong log type and returns nothing.
  let sankeyFetchToken = 0

  // Fetch Sankey data for a specific log type
  async function fetchSankeyData(logType) {
    const token = ++sankeyFetchToken
    sankeyLoading.value = true

    const ipQuery = props.data.ip
    const inboundQuery = `id.resp_h:${ipQuery}`
    const outboundQuery = `id.orig_h:${ipQuery}`

    try {
      const [
        inboundPortsRes,
        inboundIpsRes,
        inboundLinksRes,
        outboundPortsRes,
        outboundIpsRes,
        outboundLinksRes
      ] = await Promise.all([
        // Inbound: traffic TO this IP
        Events.aggregate(inboundQuery, [logType], 'id.resp_p', ['terms'], 100),
        Events.aggregate(inboundQuery, [logType], 'id.orig_h', ['terms'], 100),
        // nested_terms (parent=source IP, child=dest port) returns real top
        // (IP, port) pairs by count — composite is key-ordered + capped, which
        // dropped the high-volume pairs and forced the Sankey to fabricate edges.
        Events.aggregate(inboundQuery, [logType], null, ['nested_terms'], 30, null, ['id.orig_h', 'id.resp_p']),
        // Outbound: traffic FROM this IP
        Events.aggregate(outboundQuery, [logType], 'id.resp_p', ['terms'], 100),
        Events.aggregate(outboundQuery, [logType], 'id.resp_h', ['terms'], 100),
        // nested_terms (parent=dest port, child=dest IP) returns real top
        // (port, IP) pairs by count — this is the pairing the Port→IP edges use.
        Events.aggregate(outboundQuery, [logType], null, ['nested_terms'], 30, null, ['id.resp_p', 'id.resp_h'])
      ])

      if (token !== sankeyFetchToken) return

      sankeyInbound.value = {
        portTotals: inboundPortsRes.status === 'success' ? (inboundPortsRes.data?.terms || []) : [],
        ipTotals: inboundIpsRes.status === 'success' ? (inboundIpsRes.data?.terms || []) : [],
        links: inboundLinksRes.status === 'success' ? (inboundLinksRes.data?.composite || []) : []
      }

      sankeyOutbound.value = {
        portTotals: outboundPortsRes.status === 'success' ? (outboundPortsRes.data?.terms || []) : [],
        ipTotals: outboundIpsRes.status === 'success' ? (outboundIpsRes.data?.terms || []) : [],
        links: outboundLinksRes.status === 'success' ? (outboundLinksRes.data?.composite || []) : []
      }
    } catch (err) {
      if (token !== sankeyFetchToken) return
      console.error('Failed to load sankey data:', err)
      sankeyInbound.value = { portTotals: [], ipTotals: [], links: [] }
      sankeyOutbound.value = { portTotals: [], ipTotals: [], links: [] }
    } finally {
      // Only the newest batch owns the spinner — otherwise an early-returning
      // stale batch clears it while the current one is still in flight.
      if (token === sankeyFetchToken) sankeyLoading.value = false
    }
  }

  // Handle log type toggle change
  function handleLogTypeToggle(logType) {
    if (logType !== selectedSankeyLogType.value) {
      selectedSankeyLogType.value = logType
      fetchSankeyData(logType)
    }
  }

  function getLabel(tag) {
    if (tag.includes("bruteforce") || tag.includes("malicious") || tag.includes("crime") || tag.includes("c2")) return "bad"
    else if (tag.includes("tor") || tag.includes("vpn") || tag.includes("anonymization") || tag.includes("proxy") || tag.includes("probing") || tag.includes("scanning")) return "warning"
  }

  const malicious = computed(() => {
    if (!props.data.behavior || !props.data.behavior.tags) { return false }
    return props.data.behavior.tags.includes("bruteforce") ||
          props.data.behavior.tags.includes("malicious") ||
          props.data.behavior.tags.includes("crime") ||
          props.data.behavior.tags.includes("c2")
  })

  const anonymization = computed(() => {
    if (!props.data.behavior || !props.data.behavior.tags) { return false }
    return props.data.behavior.tags.includes("tor") ||
          props.data.behavior.tags.includes("vpn") ||
          props.data.behavior.tags.includes("anonymization") ||
          props.data.behavior.tags.includes("proxy")
  })

  const suspicious = computed(() => {
    if (!props.data.behavior || !props.data.behavior.tags) { return false }
    return (props.data.behavior.tags.includes("scanning") ||
          props.data.behavior.tags.includes("probing")) && !malicious.value
  })

  onMounted(async () => {
    // Use allSettled so that a 4xx (e.g. the org doesn't have access to
    // anonymization providers) doesn't abort the rest of the page. Failed
    // calls just render as if there were no results.
    const [anonProvidersRes] = await Promise.allSettled([
      Ip.IpAnonymizationProviders(props.data.ip)
    ])

    anonymizationProviders.value = anonProvidersRes.status === 'fulfilled' ? (anonProvidersRes.value.data || []) : []
    anonLoading.value = false

    // Fetch histogram and sankey data for this IP (requires Elite tier or higher)
    // accountType 4 = Elite, 5 = Enterprise, 1337 = Admin
    if (hasEventAccess.value) {
      const ipQuery = props.data.ip

      // Fetch histogram (broad search across all fields mentioning this IP)
      Events.search(ipQuery, 0, 0, [])
        .then(histogramResponse => {
          if (histogramResponse.status === 'success') {
            histogram.value = histogramResponse.data.histogram || []
            sourceCounts.value = histogramResponse.data.source_log_types || {}
            const logTypes = histogramResponse.data.log_types || []
            availableLogTypes.value = logTypes.sort((a, b) => b.count - a.count)
          }
        })
        .catch(err => console.error('Failed to load histogram:', err))
        .finally(() => {
          histogramLoading.value = false
          // The shared InteractiveHistogram sizes its echarts canvas once on mount;
          // re-fit it to the settled card size after this row renders so tall bars
          // aren't clipped (same guard the sibling selector views use).
          nextTick(() => requestAnimationFrame(() => histogramChart.value?.resize?.()))
        })

      // Fetch connection-specific log types (narrow: only direct src/dst connections)
      // Then fetch Sankey data for the best available log type
      Events.search(`id.resp_h:${ipQuery} OR id.orig_h:${ipQuery}`, 0, 0, [])
        .then(response => {
          if (response.status === 'success') {
            const logTypes = response.data.log_types || []
            connectionLogTypes.value = logTypes.sort((a, b) => b.count - a.count)

            // If 'conn' isn't available, select the first available type
            if (connectionLogTypes.value.length > 0 && !connectionLogTypes.value.find(lt => lt.log_type === 'conn')) {
              selectedSankeyLogType.value = connectionLogTypes.value[0].log_type
            }

            // Only fetch Sankey if there are connection log types; otherwise
            // resolve the loading state so the empty state can render.
            if (connectionLogTypes.value.length > 0) {
              fetchSankeyData(selectedSankeyLogType.value)
            } else {
              sankeyLoading.value = false
            }
          } else {
            sankeyLoading.value = false
          }
        })
        .catch(err => {
          console.error('Failed to load connection log types:', err)
          sankeyLoading.value = false
        })
        .finally(() => { connectionLogTypesLoading.value = false })
    }
  })

  // Navigation handlers
  // Handle histogram bar click (timestamp and interval are in milliseconds)
  function handleHistogramBarClick({ timestamp, interval }) {
    router.push({
      name: 'EventSearch',
      query: {
        query: props.data.ip,
        start: timestamp.toString(),
        end: (timestamp + interval).toString()
      }
    })
  }

  // Handle histogram brush select (times are in milliseconds)
  function handleHistogramBrushSelect({ startTime, endTime }) {
    router.push({
      name: 'EventSearch',
      query: {
        query: props.data.ip,
        start: startTime.toString(),
        end: endTime.toString()
      }
    })
  }

  function viewAllNetflowEvents() {
    router.push({
      name: 'EventSearch',
      query: {
        query: props.data.ip
      }
    })
  }

  function handleSankeyNodeClick({ type, value, direction, excludeIps, excludePorts }) {
    mainStore.close_slideover()

    let query = ''
    const focusIp = props.data.ip

    if (type === 'other') {
      // "Other" node - could be "Other IPs" or "Other Ports"
      if (excludeIps && excludeIps.length > 0) {
        // "Other IPs" node
        const exclusions = excludeIps.map(ip => `id.${direction === 'source' ? 'orig' : 'resp'}_h:${ip}`).join(' OR ')
        if (direction === 'source') {
          query = `id.resp_h:${focusIp} AND NOT (${exclusions})`
        } else {
          query = `id.orig_h:${focusIp} AND NOT (${exclusions})`
        }
      } else if (excludePorts && excludePorts.length > 0) {
        // "Other Ports" node
        const exclusions = excludePorts.map(p => `id.resp_p:${p}`).join(' OR ')
        if (direction === 'source') {
          query = `id.resp_h:${focusIp} AND NOT (${exclusions})`
        } else {
          query = `id.orig_h:${focusIp} AND NOT (${exclusions})`
        }
      }
    } else if (type === 'ip') {
      if (direction === 'source') {
        query = `id.orig_h:${value} AND id.resp_h:${focusIp}`
      } else {
        query = `id.resp_h:${value} AND id.orig_h:${focusIp}`
      }
    } else if (type === 'port') {
      if (direction === 'source') {
        query = `id.resp_p:${value} AND id.resp_h:${focusIp}`
      } else {
        query = `id.resp_p:${value} AND id.orig_h:${focusIp}`
      }
    }

    if (query) {
      router.push({
        name: 'EventSearch',
        query: { query, logType: selectedSankeyLogType.value }
      })
    }
  }

  function handleSankeyEdgeClick({ source, target }) {
    mainStore.close_slideover()

    let query = ''
    const focusIp = props.data.ip

    if (!source || !target) return

    // Determine if we're dealing with inbound (source direction) or outbound (dest direction)
    const isInbound = source.direction === 'source' || target.direction === 'source'

    // Helper to build exclusion clause
    const buildIpExclusion = (ips, field) => ips.map(ip => `${field}:${ip}`).join(' OR ')
    const buildPortExclusion = (ports, field) => ports.map(p => `${field}:${p}`).join(' OR ')

    if (isInbound) {
      // Inbound edges: IP -> Port or Port -> Center
      if (source.type === 'ip' && target.type === 'port') {
        query = `id.orig_h:${source.value} AND id.resp_p:${target.value} AND id.resp_h:${focusIp}`
      } else if (source.type === 'ip' && target.type === 'other' && target.excludePorts) {
        // IP -> Other Ports
        const exclusions = buildPortExclusion(target.excludePorts, 'id.resp_p')
        query = `id.orig_h:${source.value} AND id.resp_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludeIps && target.type === 'port') {
        // Other IPs -> Port
        const exclusions = buildIpExclusion(source.excludeIps, 'id.orig_h')
        query = `id.resp_p:${target.value} AND id.resp_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludeIps && target.type === 'other' && target.excludePorts) {
        // Other IPs -> Other Ports
        const ipExcl = buildIpExclusion(source.excludeIps, 'id.orig_h')
        const portExcl = buildPortExclusion(target.excludePorts, 'id.resp_p')
        query = `id.resp_h:${focusIp} AND NOT (${ipExcl}) AND NOT (${portExcl})`
      } else if (source.type === 'port' && target.direction === 'center') {
        query = `id.resp_p:${source.value} AND id.resp_h:${focusIp}`
      } else if (source.type === 'other' && source.excludePorts && target.direction === 'center') {
        // Other Ports -> Center
        const exclusions = buildPortExclusion(source.excludePorts, 'id.resp_p')
        query = `id.resp_h:${focusIp} AND NOT (${exclusions})`
      }
    } else {
      // Outbound edges: Center -> Port or Port -> IP
      if (source.direction === 'center' && target.type === 'port') {
        query = `id.orig_h:${focusIp} AND id.resp_p:${target.value}`
      } else if (source.direction === 'center' && target.type === 'other' && target.excludePorts) {
        // Center -> Other Ports
        const exclusions = buildPortExclusion(target.excludePorts, 'id.resp_p')
        query = `id.orig_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'port' && target.type === 'ip') {
        query = `id.orig_h:${focusIp} AND id.resp_p:${source.value} AND id.resp_h:${target.value}`
      } else if (source.type === 'port' && target.type === 'other' && target.excludeIps) {
        // Port -> Other IPs
        const exclusions = buildIpExclusion(target.excludeIps, 'id.resp_h')
        query = `id.orig_h:${focusIp} AND id.resp_p:${source.value} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludePorts && target.type === 'ip') {
        // Other Ports -> IP
        const exclusions = buildPortExclusion(source.excludePorts, 'id.resp_p')
        query = `id.resp_h:${target.value} AND id.orig_h:${focusIp} AND NOT (${exclusions})`
      } else if (source.type === 'other' && source.excludePorts && target.type === 'other' && target.excludeIps) {
        // Other Ports -> Other IPs
        const portExcl = buildPortExclusion(source.excludePorts, 'id.resp_p')
        const ipExcl = buildIpExclusion(target.excludeIps, 'id.resp_h')
        query = `id.orig_h:${focusIp} AND NOT (${portExcl}) AND NOT (${ipExcl})`
      }
    }

    if (query) {
      router.push({
        name: 'EventSearch',
        query: { query, logType: selectedSankeyLogType.value }
      })
    }
  }

  // Source nodes drill into Event Search scoped to that source. `source` is not a
  // queryable field — it resolves to index patterns via the `sources` param.
  function handleSourceNodeClick({ source }) {
    mainStore.close_slideover()
    router.push({
      name: 'EventSearch',
      query: { query: props.data.ip, sources: `s:${source}`, group: 'source' }
    })
  }

  // Get tags from OSINT service
  function getServiceTags(service) {
    if (!service.tags || !Array.isArray(service.tags)) return []
    return service.tags
  }
</script>

<template>
  <div class="ip-view-container" :class="{ embedded: hideIdentity }">
    <div class="container">
      <!-- IP Header Section (hidden when the host already shows the IP) -->
      <div class="ip-header" v-if="!hideIdentity" data-guide="ip-header">
        <div class="ip-header-content">
          <h1 class="ip-address">{{ props.data.ip }}</h1>
          <div class="location-info" v-if="location">
            <img
              height="16"
              v-if="location.country_iso"
              class="country-flag"
              :src="require('@/assets/flags/4x3/' + location.country_iso.toLowerCase() + '.svg')"
            >
            <span class="location-text">
              <span v-if="location.city">{{ location.city }}, </span>
              {{ location.country_iso }}
            </span>
          </div>
        </div>
      </div>

      <!-- Widgets grid -->
      <div class="top-cards-grid" data-guide="ip-cards">
        <!-- Geo / Country map widget -->
        <IpGeoMap v-if="hasEnrichment('webscout_geo') && location" :location="location" class="geo-widget" />

        <!-- Geo widget empty state (enrichment ran, but no location) -->
        <div v-else-if="hasEnrichment('webscout_geo')" class="info-card geo-empty-card geo-widget">
          <div class="card-header">
            <span class="card-label">Location</span>
          </div>
          <div class="card-body">
            <div class="card-empty">No location data for this IP</div>
          </div>
        </div>

        <!-- Infrastructure Card -->
        <div v-if="hasEnrichment('webscout_geo')" class="info-card infrastructure-card">
          <div class="card-header">
            <span class="card-label">Infrastructure</span>
            <h3 class="card-title">{{ company?.name || 'Unknown' }}</h3>
          </div>
          <div class="card-body">
            <p class="card-description">{{ company?.description || 'No description available' }}</p>

            <!-- Network Details -->
            <div class="infrastructure-details">
              <div class="detail-row" v-if="asInfo && asInfo.as_number">
                <span class="detail-label">AS Number</span>
                <span class="detail-value">
                  AS{{ asInfo.as_number }}
                </span>
              </div>
              <div class="detail-row" v-if="asInfo && asInfo.organization">
                <span class="detail-label">Organization</span>
                <span class="detail-value">{{ asInfo.organization }}</span>
              </div>
              <div class="detail-row" v-if="network && network.type">
                <span class="detail-label">Network Type</span>
                <span class="detail-value">{{ network.type }}</span>
              </div>
              <div class="detail-row" v-if="network && network.range">
                <span class="detail-label">Network Range</span>
                <span class="detail-value">{{ network.range }}</span>
              </div>
              <div class="detail-row" v-if="props.data.operator">
                <span class="detail-label">Operator</span>
                <span class="detail-value">{{ props.data.operator }}</span>
              </div>
              <div class="detail-row" v-if="props.data.network_provider">
                <span class="detail-label">Network Provider</span>
                <span class="detail-value">{{ props.data.network_provider }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Anonymization Providers Card (loading → list → empty) -->
        <div v-if="hasEnrichment('webscout_anonymization')" class="info-card anonymization-card">
          <div class="card-header">
            <span class="card-label">Anonymization</span>
          </div>

          <div class="card-body">
            <!-- Loading (providers fetched separately from the enrichment blob) -->
            <div v-if="anonLoading" class="card-loading">
              <font-awesome-icon icon="spinner" spin />
            </div>

            <!-- Provider List -->
            <div v-else-if="anonymizationProviders && anonymizationProviders.length > 0" class="providers-compact-list">
              <div
                v-for="(provider, index) in anonymizationProviders"
                :key="index"
                class="provider-compact-item"
              >
                <ProviderThumbnail
                  :data="{
                    name: provider.display_name || provider.provider,
                    thumbnail_url: provider.thumbnail_url
                  }"
                />
                <div class="provider-compact-info">
                  <span class="provider-compact-name">{{ provider.display_name || provider.provider }}</span>
                  <span class="provider-compact-meta">{{ provider.type }}</span>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else class="card-empty">No anonymization providers detected</div>
          </div>
        </div>

        <!-- OSINT Providers Card -->
        <div v-if="hasEnrichment('webscout_osint')" class="info-card osint-card">
          <div class="card-header">
            <span class="card-label">OSINT</span>
          </div>

          <!-- Provider List -->
          <div class="card-body">
            <div v-if="osint && osint.services && osint.services.length > 0" class="providers-compact-list">
              <div
                v-for="service in osint.services"
                :key="service.provider"
                class="provider-compact-item"
              >
                <ProviderThumbnail
                  :data="{
                    name: service.display_name || service.provider,
                    thumbnail_url: service.thumbnail_url
                  }"
                />
                <div class="provider-compact-info">
                  <div class="provider-name-line">
                    <span class="provider-compact-name">{{ service.display_name || service.provider }}</span>
                    <div class="provider-tags ms-auto" v-if="getServiceTags(service).length > 0">
                      <span
                        v-for="tag in getServiceTags(service)"
                        :key="tag"
                        class="provider-tag"
                        :class="getLabel(tag)"
                      >
                        {{ tag }}
                      </span>
                    </div>
                  </div>
                  <span class="provider-compact-meta" v-if="service.last_seen">
                    {{ moment(service.last_seen).fromNow() }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else class="card-empty">No OSINT mentions found</div>
          </div>
        </div>

        <!-- Signals Card -->
        <div v-if="hasEnrichment('webscout_signals')" class="info-card signals-card">
          <div class="card-header">
            <span class="card-label">Signals</span>
          </div>

          <!-- Signal List -->
          <div class="card-body">
            <div v-if="signals.length > 0" class="providers-compact-list">
              <div
                v-for="signal in signals"
                :key="signal.id"
                class="provider-compact-item signal-compact-item"
                @click="openSignal(signal)"
              >
                <div class="provider-compact-info">
                  <span class="provider-compact-name">{{ signal.title }}</span>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else class="card-empty">No signals for this IP</div>
          </div>
        </div>

        <!-- AbuseIPDB Card -->
        <div v-if="hasEnrichment('abuseipdb')" class="info-card abuse-card">
          <div class="card-header">
            <span class="card-label">AbuseIPDB</span>
            <h3 v-if="abuse && abuse.abuse_confidence_score != null" class="card-title abuse-score-title" :class="`sev-${abuseSeverity}`">
              {{ abuse.abuse_confidence_score }}%
              <span class="abuse-score-caption">confidence of abuse</span>
            </h3>
          </div>

          <div class="card-body">
            <div v-if="abuse && abuse.abuse_confidence_score != null" class="infrastructure-details">
              <div class="detail-row">
                <span class="detail-label">Reports</span>
                <span class="detail-value">{{ (abuse.total_reports || 0).toLocaleString() }}</span>
              </div>
              <div class="detail-row" v-if="abuse.last_reported_at">
                <span class="detail-label">Last Reported</span>
                <span class="detail-value">{{ moment(abuse.last_reported_at).fromNow() }}</span>
              </div>
              <div class="detail-row" v-if="abuse.country_code">
                <span class="detail-label">Country</span>
                <span class="detail-value">{{ abuse.country_code }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Whitelisted</span>
                <span class="detail-value">{{ abuse.is_whitelisted ? 'Yes' : 'No' }}</span>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else class="card-empty">No AbuseIPDB reports</div>
          </div>
        </div>
      </div>

      <!-- Netflow + Network connection widgets (side by side, not full-width) -->
      <div class="chart-grid">
      <!-- Netflow Observations Section -->
      <section v-if="hasEventAccess" class="content-section widget-section chart-widget" data-guide="ip-netflow">
        <div class="section-header">
          <div class="section-heading">
            <h3 class="section-title">Observations</h3>
            <span class="section-subtitle">Event activity across all observed data</span>
          </div>
          <a @click.prevent="viewAllNetflowEvents" class="view-all-link">View all</a>
        </div>

        <div class="widget-body">
          <div class="chart-fill">
            <!-- Loading state -->
            <div v-if="histogramLoading" class="chart-state loading">
              <font-awesome-icon icon="spinner" spin />
            </div>

            <!-- Histogram -->
            <InteractiveHistogram
              v-else-if="histogram && histogram.length > 0"
              ref="histogramChart"
              :data="histogram"
              @bar-click="handleHistogramBarClick"
              @brush-select="handleHistogramBrushSelect"
              height="100%"
            />

            <!-- Empty state -->
            <div v-else class="chart-state">
              No events observed for this IP
            </div>
          </div>
        </div>
      </section>

      <!-- Network Connections Sankey Chart Section -->
      <section v-if="hasEventAccess" class="content-section widget-section chart-widget" data-guide="ip-connections">
        <div class="section-header">
          <div class="section-header-left">
            <span class="section-subtitle">Direct source and destination connections to and from this IP</span>
            <h2 class="section-title">Network connections</h2>
          </div>
          <a @click.prevent="viewAllNetflowEvents" class="view-all-link">View all</a>
        </div>

        <div class="widget-body">
          <div class="chart-fill">
            <!-- Loading state -->
            <div v-if="sankeyLoading || connectionLogTypesLoading" class="chart-state loading">
              <font-awesome-icon icon="spinner" spin />
            </div>

            <!-- Sankey Chart -->
            <SankeyFlowChart
              v-else-if="sankeyInbound.portTotals.length > 0 || sankeyOutbound.portTotals.length > 0"
              :inbound="sankeyInbound"
              :outbound="sankeyOutbound"
              :centeredIp="props.data.ip"
              :maxPorts="10"
              :maxIps="20"
              height="100%"
              @node-click="handleSankeyNodeClick"
              @edge-click="handleSankeyEdgeClick"
            />

            <!-- Empty state: no connection data, but observations exist -->
            <div v-else-if="connectionLogTypes.length === 0 && !connectionLogTypesLoading && availableLogTypes.length > 0" class="empty-state-netflow">
              <p>No direct network connections found</p>
              <p class="empty-state-hint">Netflow observations for this IP are likely from non-connection fields such as DNS answers, HTTP headers, or certificate data</p>
            </div>

            <!-- Empty state: no connection data at all -->
            <div v-else-if="connectionLogTypes.length === 0 && !connectionLogTypesLoading" class="empty-state-netflow">
              <p>No network connection data available</p>
            </div>

            <!-- Empty state: current log type has no Sankey data but others might -->
            <div v-else class="empty-state-netflow">
              <p>No data for {{ selectedSankeyLogType }} connections</p>
            </div>
          </div>

          <!-- Log type toggles below the graph; scroll horizontally when many -->
          <div v-if="connectionLogTypes.length > 0 || connectionLogTypesLoading" class="log-type-toggles">
            <!-- Loading placeholder while the connection log types resolve -->
            <span
              v-if="connectionLogTypes.length === 0 && connectionLogTypesLoading"
              class="log-type-loading"
            >
              <font-awesome-icon icon="spinner" spin />
            </span>
            <!-- Show log types from connection-specific search -->
            <button
              v-for="lt in connectionLogTypes"
              :key="lt.log_type"
              class="log-type-toggle"
              :class="{ active: selectedSankeyLogType === lt.log_type }"
              @click="handleLogTypeToggle(lt.log_type)"
            >
              <span class="log-type-color" :style="{ backgroundColor: getLogTypeColor(lt.log_type) }"></span>
              <span class="log-type-name">{{ lt.log_type }}</span>
              <span class="log-type-count">{{ lt.count.toLocaleString() }}</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Sources: which sources observed this IP — 3rd item in the 2-col grid, so
           it lands at col 1 / row 2, directly under the Observations widget. -->
      <section v-if="hasEventAccess" class="content-section">
        <SourceSelectorSankey
          :source-counts="sourceCounts"
          :selector="props.data.ip"
          :loading="histogramLoading"
          @source-click="handleSourceNodeClick"
        />
      </section>

      <!-- Netflow & Network Connections Upgrade Prompt -->
      <section v-if="!hasEventAccess" class="content-section widget-section">
        <div class="section-header">
          <h2 class="section-title">Netflow observations</h2>
        </div>
        <div class="upgrade-prompt">
          <font-awesome-icon :icon="['fas', 'lock']" class="upgrade-icon" />
          <p class="upgrade-message">Netflow observations require an Elite or Enterprise plan.</p>
        </div>
      </section>

      <section v-if="!hasEventAccess" class="content-section widget-section">
        <div class="section-header">
          <h2 class="section-title">Network connections</h2>
        </div>
        <div class="upgrade-prompt">
          <font-awesome-icon :icon="['fas', 'lock']" class="upgrade-icon" />
          <p class="upgrade-message">Network connections require an Elite or Enterprise plan.</p>
        </div>
      </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Jura:wght@300;400;500;600;700&display=swap');

/* Container - No background, clean like Providers.vue */
.ip-view-container {
  min-height: 100vh;
  color: var(--text-primary);
}

/* Embedded in the selector single view: don't reserve a full viewport, or a
   sibling widget (e.g. the API-output preview) gets pushed far below the fold. */
.ip-view-container.embedded {
  min-height: 0;
}

.container {
  width: 100%;
  max-width: none;
  margin: 0;
  /* No inner padding — widgets align flush with the host's header bar. */
  padding: 0;
}

/* IP Header Section */
.ip-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2.5rem;
}

.ip-header-content {
  flex: 1;
}

.ip-address {
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-heading);
  margin: 0 0 0.5rem 0;
  font-family: 'Jura', sans-serif;
}

.location-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.95rem;
}

.country-flag {
  border-radius: var(--radius-xs);
}

/* Widgets grid — auto-filling dark tiles, matching /flow/integrations. */
.top-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(325px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

/* Geo map is a featured widget (2 tracks) but never full-bleed. */
.geo-widget {
  grid-column: span 2;
}

.info-card {
  background: var(--surface-card);
  border: 1px solid transparent;
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  max-height: 420px;
}

.card-header {
  margin-bottom: 1rem;
  flex-shrink: 0;
}

/* Scrollable card body - header stays pinned above it */
.card-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
}

.card-body::-webkit-scrollbar {
  width: 6px;
}

.card-body::-webkit-scrollbar-track {
  background: transparent;
}

.card-body::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-xs);
}

.card-body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* Loading placeholder inside a card body (e.g. anonymization fetch) */
.card-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  font-size: 1.25rem;
  color: var(--text-secondary);
}

/* Empty state inside an info-card body — the enrichment ran but returned no
   data, so the card stays visible with a muted placeholder instead of vanishing. */
.card-empty {
  font-size: 0.8125rem;
  color: var(--text-faint);
  font-style: italic;
  padding: 0.25rem 0;
}

/* Geo widget empty state fills the same 2-column slot as the map. */
.geo-empty-card {
  justify-content: flex-start;
  min-height: 180px;
}

.card-label {
  display: block;
  font-size: 0.75rem;
  color: var(--text-faint);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-heading);
  margin: 0;
}

/* AbuseIPDB card — reuses .card-title / .detail-row like the Infrastructure card,
   with the confidence score coloured by severity. */
.abuse-score-title.sev-none {
  color: var(--text-muted);
}

.abuse-score-title.sev-low {
  color: var(--color-success-text);
}

.abuse-score-title.sev-medium {
  color: var(--color-warning-text);
}

.abuse-score-title.sev-high {
  color: var(--color-error-text);
}

.abuse-score-caption {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-faint);
  text-transform: none;
  letter-spacing: 0;
}

.card-description {
  color: var(--text-muted);
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.card-links {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.card-link {
  color: var(--text-faint);
  font-size: 0.875rem;
  text-decoration: underline;
  transition: color 0.2s;
}

.card-link:hover {
  color: var(--text-primary);
}

.card-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.info-tag {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: var(--surface-overlay-2);
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* Content Sections - Minimal styling, no boxes */
.content-section {
  margin-bottom: 3rem;
}

/* Widget shell — dark tile matching /flow/integrations, for the
   Netflow/Sankey/upgrade sections. */
.widget-section {
  background: var(--surface-card);
  border: 1px solid transparent;
  border-radius: var(--radius-xl);
  padding: 1.5rem;
}

.widget-section .section-header {
  margin-bottom: 1.25rem;
}

/* The two event widgets share a row rather than stacking full-width. */
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  align-items: start;
}

.chart-grid > .content-section {
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .chart-grid {
    grid-template-columns: 1fr;
  }
}

/* Fixed-height chart tiles so the side-by-side row stays even regardless of
   loading/empty/chart state. The body flexes and the chart fills it. */
.chart-widget {
  /* Fixed height, sized to give the connections Sankey room for its nodes
     without cramping on busy IPs. Both row widgets share it so they align. */
  height: 620px;
  display: flex;
  flex-direction: column;
}

.chart-widget .widget-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chart-widget .chart-fill {
  flex: 1;
  min-height: 0;
  position: relative;
  /* Contain the shared InteractiveHistogram, which sizes its echarts canvas once
     on mount and can otherwise spill past the card edges (same guard the sibling
     selector views use on their observations chart-fill). */
  overflow: hidden;
}

.chart-widget .chart-fill > .empty-state-netflow {
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-heading);
  margin: 0;
}

/* Kicker-above-title stack, matching the info-card headers. */
.section-header-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;
}

.section-subtitle {
  color: var(--text-faint);
  font-size: 0.8rem;
  margin: 0;
}

/* Title-above-subtitle heading + centered chart placeholders, matching the
   Observations widget in the sibling selector views (DomainView et al.). */
.section-heading {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.chart-state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-faint);
  font-size: 0.85rem;
  font-style: italic;
  text-align: center;
  padding: 1rem;
}

.chart-state.loading {
  font-size: 1.5rem;
  color: var(--text-secondary);
  font-style: normal;
}

/* Pill "View all", matching the sibling selector views. */
.view-all-link {
  border: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-secondary);
  border-radius: var(--radius-pill);
  padding: 0.3rem 0.9rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
  text-decoration: none;
}

.view-all-link:hover {
  color: var(--accent-text);
  border-color: var(--accent);
}

/* Details List - Simple, no box */
.details-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: var(--text-faint);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: right;
}

/* Behavior Tags */
.behavior-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.behavior-tag {
  display: inline-block;
  padding: 0.35rem 0.85rem;
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  background-color: var(--surface-overlay-2);
  color: var(--text-muted);
  transition: all 0.2s;
}

.behavior-tag.warning {
  background-color: rgba(252, 236, 136, 0.1);
  color: var(--color-warning-text);
}

.behavior-tag.bad {
  background-color: rgba(252, 140, 136, 0.1);
  color: var(--color-error-text);
}

/* Ports List - Clean, minimal */
.ports-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.port-item {
  display: grid;
  grid-template-columns: 80px 100px 1fr;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.9rem;
}

.port-item:last-child {
  border-bottom: none;
}

.port-number {
  color: var(--text-heading);
  font-weight: 500;
}

.port-protocol {
  color: var(--text-muted);
}

.port-description {
  color: var(--text-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Infrastructure Details within card */
.infrastructure-details {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .detail-label {
  color: var(--text-faint);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-row .detail-value {
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: right;
}

/* Responsive */
@media (max-width: 768px) {
  .ip-header {
    flex-direction: column;
    gap: 1rem;
  }

  .top-cards-grid {
    grid-template-columns: 1fr;
  }

  .geo-widget {
    grid-column: auto;
  }

  .port-item {
    grid-template-columns: 60px 80px 1fr;
    gap: 0.5rem;
  }
}

/* Anonymization Card - Compact Provider List */
.providers-compact-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.provider-compact-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: var(--radius-md);
}

.signal-compact-item {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.signal-compact-item:hover {
  background-color: rgba(255, 255, 255, 0.06);
}

.provider-compact-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.provider-name-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.provider-compact-name {
  font-size: 0.9rem;
  color: var(--text-primary);
  font-weight: 500;
}

.provider-compact-meta {
  font-size: 0.75rem;
  color: var(--text-faint);
  text-transform: uppercase;
}

.provider-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
}

.provider-tag {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: var(--radius-xs);
  font-size: 0.7rem;
  background-color: var(--surface-overlay-2);
  color: var(--text-muted);
  transition: all 0.2s;
}

.provider-tag.warning {
  background-color: rgba(252, 236, 136, 0.1);
  color: var(--color-warning-text);
}

.provider-tag.bad {
  background-color: rgba(252, 140, 136, 0.1);
  color: var(--color-error-text);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-faint);
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

/* Empty State for Netflow */
.empty-state-netflow {
  text-align: center;
  padding: 3rem;
  color: var(--text-faint);
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-lg);
}

.empty-state-netflow p {
  margin: 0;
  font-size: 0.95rem;
}

.empty-state-hint {
  margin-top: 0.5rem !important;
  font-size: 0.8rem !important;
  color: var(--text-disabled);
}

/* Section Count */
.section-count {
  color: var(--text-faint);
  font-size: 0.875rem;
  font-weight: 500;
}

/* Upgrade Prompt */
.upgrade-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-lg);
  text-align: center;
}

.upgrade-icon {
  font-size: 2rem;
  color: var(--text-faint);
  margin-bottom: 1rem;
  opacity: 0.6;
}

.upgrade-message {
  color: var(--text-muted);
  font-size: 0.95rem;
  margin: 0 0 1rem 0;
}

.upgrade-link {
  color: var(--accent);
  font-size: 0.9rem;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.upgrade-link:hover {
  color: var(--accent-text);
}

/* Log Type Toggles */
.log-type-toggles {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: var(--surface-control) transparent;
}

.log-type-toggles::-webkit-scrollbar {
  height: 6px;
}

.log-type-toggles::-webkit-scrollbar-track {
  background: transparent;
}

.log-type-toggles::-webkit-scrollbar-thumb {
  background: var(--surface-control);
  border-radius: var(--radius-xs);
}

.log-type-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition);
  font-family: inherit;
  flex-shrink: 0;
  white-space: nowrap;
}

/* Inline placeholder occupying a toggle's slot in the strip. Deliberately not
   .chart-state — this row is thin, and an absolutely-positioned centred box
   would escape it. */
.log-type-loading {
  display: inline-flex;
  align-items: center;
  padding: 0.6rem 0.75rem;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.log-type-toggle:hover {
  background-color: var(--surface-overlay-2);
}

.log-type-toggle.active {
  background-color: rgba(115, 87, 210, 0.2);
  border-color: rgba(115, 87, 210, 0.4);
}

.log-type-toggle:disabled {
  cursor: default;
  opacity: 0.7;
}

.log-type-color {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-xs);
  flex-shrink: 0;
}

.log-type-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
}

.log-type-toggle.active .log-type-name {
  color: var(--text-heading);
}

.log-type-count {
  font-size: 0.75rem;
  color: var(--text-faint);
}

.log-type-toggle.active .log-type-count {
  color: var(--text-muted);
}
</style>
